import React, { useEffect, useMemo, useState } from "react";

import { Box, Divider, Drawer, IconButton, TextField, Typography } from "@mui/material";
import { Close, LocalOffer, Percent } from "@mui/icons-material";

import { BlackCrown } from "../../../Pages/Invitations/styles";
import { CheckoutDrawerPaper, CheckoutRow, CouponRow, DiscountChip, TotalRow } from "./styles";
import {
	usePostCreateIntentMutation,
	useLazyGetInvitationPlanPriceIdQuery
} from "../../../store/services/payment/payment.service";
import ButtonComponent from "../../ButtonComponent";
import { ExchangeRate, PaymentIntent } from "../../../store/services/payment/dto/payment.dto";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { trackEvent } from "../../../utils/analytics";
import { trackMetaEvent } from "../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../utils/tiktokPixel";
import { useAppSelector } from "../../../store";
import { getFullState } from "../../../store/slices/auth/auth.slice";
import { getCouponCookie, setCouponCookie } from "../../../utils/affiliateCoupon";
import { useLazyValidateCouponQuery } from "../../../store/services/affiliate/affiliate.service";
import { AffiliateCreateCouponDTO } from "../../../store/services/affiliate/dto/affiliate.dto";
import { getDiscountAmount } from "../../../utils/getInvitationPrice";
import { formatCurrency } from "../../../utils/formatCurrency";
import { round } from "lodash";
import { usePaddleCheckout } from "../../../hooks/usePaddleCheckout";

export interface CheckoutRowItem {
	icon: () => React.ReactElement;
	label: string;
	sublabel?: string;
	originalAmount?: string;
	amount: string;
}

interface CheckoutDrawerProps {
	open: boolean;
	onClose: () => void;
	rows: CheckoutRowItem[];
	totalLabel: string;
	intentData: PaymentIntent;
	discountLabel?: string;
	exchangeRate?: ExchangeRate;
}

const CheckoutDrawer = ({
	open,
	onClose,
	rows,
	totalLabel,
	intentData,
	discountLabel,
	exchangeRate
}: CheckoutDrawerProps) => {
	console.log(exchangeRate);
	const [loadingPayment, setLoadingPayment] = useState(false);
	const { user } = useAppSelector(getFullState);
	const [couponCode, setCouponCode] = useState("");
	const [couponObject, setCouponObject] = useState<AffiliateCreateCouponDTO | null>(null);
	const [couponStatus, setCouponStatus] = useState<"valid" | "invalid" | null>(null);
	const { openCheckout } = usePaddleCheckout();

	const [ValidateCoupon, { isLoading: isValidatingCoupon }] = useLazyValidateCouponQuery();

	const [callCreateIntent] = usePostCreateIntentMutation();
	const [fetchPlanPriceId, { isLoading: isFetchingPriceId }] = useLazyGetInvitationPlanPriceIdQuery();

	useEffect(() => {
		if (open && user?._id) {
			const savedCoupon = getCouponCookie(user._id);
			if (savedCoupon) {
				setCouponCode(savedCoupon);
			}
		}
	}, [open, user?._id]);
	const couponDiscountAmount = useMemo(() => {
		if (!couponObject) return 0;
		const _amount = couponObject.is_discount_percentage
			? ((couponObject.discount || 0) / 100) * intentData.amount
			: couponObject.discount || 0;
		return round(_amount, 2);
	}, [couponObject, intentData]);

	const totalUsdLabel = useMemo(() => {
		if (!exchangeRate?.rate) return null;
		const finalAmount = intentData.amount - couponDiscountAmount;
		return `≈ ${formatCurrency(round(finalAmount / exchangeRate.rate, 2), exchangeRate.base_currency)}`;
	}, [exchangeRate, intentData, couponDiscountAmount]);

	const handleValidateCoupon = () => {
		if (!couponCode.trim()) return;
		setCouponStatus(null);
		setCouponObject(null);
		ValidateCoupon({ code: couponCode || "", invitationId: intentData.invitationId }).then(({ data, error }) => {
			if (data) {
				if (data.valid && user) {
					setCouponObject(data.coupon);
					setCouponCookie(user._id, data.coupon.code);
					setCouponStatus("valid");
				} else if (!data.valid) {
					setCouponStatus("invalid");
				}
			}
			if (error) {
				notifyError(error as IError);
				setCouponStatus(null);
			}
		});
	};

	const handlePayment = async () => {
		try {
			setLoadingPayment(true);
			trackEvent("pay_intention", {
				scope: "Invitations",
				description: "Pay Wedding Invitation"
			});
			trackMetaEvent(
				"pay_intention",
				{
					invitation_id: intentData.invitationId,
					currency: intentData.currency,
					value: round(intentData.amount - couponDiscountAmount, 2)
				},
				true
			);
			trackTikTokEvent("pay_intention", {
				invitation_id: intentData.invitationId,
				currency: intentData.currency,
				value: round(intentData.amount - couponDiscountAmount, 2)
			});
			if (exchangeRate) {
				const { data: priceData, error: priceError } = await fetchPlanPriceId({
					invitationId: intentData.invitationId,
					targetPlan: intentData.planId
				});
				if (priceError || !priceData) {
					setLoadingPayment(false);
					notifyError(priceError as IError);
					return;
				}
				return openCheckout(
					{
						invitationId: intentData.invitationId,
						email: intentData.email || "",
						userId: user?._id || "",
						planId: intentData.planId,
						oldPlanId: intentData.oldPlanId
					},
					priceData.priceId
				);
			}
			const _intentData = { ...intentData };
			if (couponObject && couponDiscountAmount > 0) {
				_intentData["amount"] = round(_intentData.amount - couponDiscountAmount, 2);
				_intentData["coupon"] = couponObject._id;
			}
			callCreateIntent(_intentData).then(({ data, error }) => {
				if (data) {
					console.log(data);
					window.location.href = `https://accept.paymob.com/unifiedcheckout/?publicKey=${process.env.REACT_APP_PAYMOB_PUBLIC_KEY}&clientSecret=${data.clientSecret}`;
				}
				if (error) {
					setLoadingPayment(false);
					notifyError(error as IError);
				}
			});
		} catch (e) {
			setLoadingPayment(false);
		}
	};

	return (
		<Drawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			slotProps={{
				paper: {
					component: CheckoutDrawerPaper,
					sx: { zIndex: theme => theme.zIndex.modal + 1 }
				}
			}}
			sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
		>
			<>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
					<Box>
						<Typography variant="h6" fontWeight={700}>
							Order Summary
						</Typography>
						{discountLabel && (
							<DiscountChip mt={0.75}>
								<LocalOffer sx={{ fontSize: 14 }} />
								<span>{discountLabel} discount applied</span>
							</DiscountChip>
						)}
					</Box>
					<IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
						<Close fontSize="small" />
					</IconButton>
				</Box>
				<Box display="flex" flexDirection="column" gap={1.5}>
					{rows.map((row, i) => (
						<CheckoutRow key={i}>
							<Box display="flex" alignItems="center" gap={1.5}>
								{row.icon && row.icon()}
								<Box>
									<Typography variant="body2" fontWeight={700} color="primary.dark">
										{row.label}
									</Typography>
									{row.sublabel && (
										<Typography variant="caption" color="text.secondary">
											{row.sublabel}
										</Typography>
									)}
								</Box>
							</Box>

							<Box textAlign="right">
								{row.originalAmount && (
									<Typography
										variant="body2"
										color="text.disabled"
										sx={{ textDecoration: "line-through", lineHeight: 1.3 }}
									>
										{row.originalAmount}
									</Typography>
								)}
								<Typography variant="h6" fontWeight={700} color="primary.dark">
									{row.amount}
								</Typography>
							</Box>
						</CheckoutRow>
					))}
				</Box>
				<Divider sx={{ my: 2 }} />
				<Box mb={2}>
					<CouponRow>
						<TextField
							size="small"
							fullWidth
							label="Coupon Code"
							placeholder="e.g. JANE15"
							value={couponCode}
							onChange={e => {
								setCouponCode(e.target.value.toUpperCase());
								setCouponStatus(null);
							}}
							error={couponStatus === "invalid"}
							inputProps={{ style: { textTransform: "uppercase" } }}
						/>
						<ButtonComponent
							variant="outlined"
							size="medium"
							onClick={handleValidateCoupon}
							disabled={!couponCode.trim() || isValidatingCoupon}
							loading={isValidatingCoupon}
							text="Validate"
						/>
					</CouponRow>
					{couponStatus && (
						<Typography
							variant="caption"
							color={couponStatus === "valid" ? "success.main" : "error"}
							mt={0.5}
							display="block"
						>
							{couponObject && couponStatus === "valid"
								? `${getDiscountAmount(couponObject.discount || 0, couponObject.currency || "EGP", couponObject.is_discount_percentage || true)} discount Coupon applied successfully`
								: "Invalid coupon code"}
						</Typography>
					)}
				</Box>
				{couponObject && (
					<>
						<TotalRow>
							<Typography variant="subtitle1" fontWeight={600} color="text.secondary">
								Sub Total
							</Typography>
							<Typography variant="h5" fontWeight={800} color="primary.dark">
								{totalLabel}
							</Typography>
						</TotalRow>
						<TotalRow my={1}>
							<Typography variant="subtitle1" fontWeight={600} color="text.secondary">
								Coupon {couponObject.code}
							</Typography>
							<Typography variant="h5" fontWeight={800} color="primary.dark">
								- {formatCurrency(couponDiscountAmount, couponObject.currency)}
							</Typography>
						</TotalRow>
					</>
				)}

				<TotalRow>
					<Typography variant="subtitle1" fontWeight={600} color="text.secondary">
						Total
					</Typography>
					<Box textAlign="right">
						<Typography variant="h5" fontWeight={800} color="primary.dark">
							{formatCurrency(intentData.amount - couponDiscountAmount, intentData.currency)}
						</Typography>
						{/*{totalUsdLabel && (*/}
						{/*	<Typography variant="body1" color="text.secondary" display="block">*/}
						{/*		{totalUsdLabel}*/}
						{/*	</Typography>*/}
						{/*)}*/}
					</Box>
				</TotalRow>
				<ButtonComponent
					variant="contained"
					fullWidth
					size="large"
					sx={{ mt: 2, borderRadius: "10px", py: 1.5, fontSize: "1rem", fontWeight: 700 }}
					onClick={handlePayment}
					disabled={loadingPayment || isFetchingPriceId}
					loading={loadingPayment || isFetchingPriceId}
					text={`Pay ${formatCurrency(intentData.amount - couponDiscountAmount, intentData.currency)}`}
				/>
			</>
		</Drawer>
	);
};

export default CheckoutDrawer;
